import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Grid,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Tabs,
  Tab,
  Avatar
} from '@mui/material';
import { 
  Flight, 
  Person, 
  CalendarToday, 
  AttachMoney,
  MoreVert as MoreVertIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Explore as ExploreIcon,
  Payment as PaymentIcon,
  OpenInNew,
  LocationOn,
  ChatBubbleOutline
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientBranding } from '../contexts/ClientBrandingContext';
import { partnerAuthName } from '../utils/clientInvite';
import api from '../config/api';
import { getInquiryDisplayTitle, isPackageExpired } from '../utils/partnerListUtils';
import {
  DOCUMENT,
  PAGE_CONTAINER_MAX_WIDTH,
  documentPageCanvasSx,
  documentPageContainerSx,
  documentSubTabsSx,
  documentTabIndicatorSx,
  documentEyebrowSx,
} from '../constants/documentTheme';
import { partnerOxbloodBtnSx, partnerOutlineBtnSx } from '../components/PartnerListChrome';
import PartnerLockup from '../components/PartnerLockup';

const bookingCardSx = {
  bgcolor: DOCUMENT.surface,
  border: `1px solid ${DOCUMENT.ruleHard}`,
  borderRadius: 0,
  boxShadow: 'none',
  cursor: 'pointer',
  '&:hover': { borderColor: DOCUMENT.accent },
};

const bookingChipSx = {
  borderRadius: 0,
  height: 24,
  fontSize: 10.5,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  fontWeight: 500,
};

const accentIconSx = { color: DOCUMENT.accent, fontSize: 18 };

const isArchivedValue = (value) => value === true || value === 1 || value === '1';

const listCardSx = (archived) => ({
  ...bookingCardSx,
  ...(archived ? {
    bgcolor: DOCUMENT.canvas,
    borderStyle: 'dashed',
    borderColor: DOCUMENT.ink3,
    '&:hover': { borderColor: DOCUMENT.ink2 },
  } : {}),
});

const MyBookings = () => {
  const { t } = useTranslation();
  const { branding } = useClientBranding();
  const brandedClient = Boolean(partnerAuthName(branding));
  const [activeTab, setActiveTab] = useState(1); // Travel Packages default
  const [bookings, setBookings] = useState([]);
  const [packageBookings, setPackageBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [packageLoading, setPackageLoading] = useState(true);
  const [inquiriesLoading, setInquiriesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [addToTripDialog, setAddToTripDialog] = useState(false);
  const [userTrips, setUserTrips] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showArchived, setShowArchived] = useState(false);
  const [hoveredBookingId, setHoveredBookingId] = useState(null);
  const [testingPayment, setTestingPayment] = useState(null);
  const [packageMenuAnchor, setPackageMenuAnchor] = useState(null);
  const [selectedPackageBooking, setSelectedPackageBooking] = useState(null);
  const [packageDeleteDialog, setPackageDeleteDialog] = useState(false);
  const [inquiryMenuAnchor, setInquiryMenuAnchor] = useState(null);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [inquiryDeleteDialog, setInquiryDeleteDialog] = useState(false);
  const [messagingBookingRef, setMessagingBookingRef] = useState(null);
  const navigate = useNavigate();
  
  // Check if running in development mode
  const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';

  // Test payment confirmation handler
  const handleTestPayment = async (booking) => {
    try {
      setTestingPayment(booking.id);
      const response = await api.post(`/package-booking/test/confirm-payment/${booking.booking_reference}`);
      if (response.data.success) {
        showSnackbar('✅ Test payment confirmed! Trip created.', 'success');
        // Refresh bookings
        fetchPackageBookings();
      }
    } catch (err) {
      console.error('Test payment error:', err);
      showSnackbar(err.response?.data?.error || 'Test payment failed', 'error');
    } finally {
      setTestingPayment(null);
    }
  };

  useEffect(() => {
    fetchBookings();
    fetchPackageBookings();
    fetchInquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/bookings/my-bookings?includeArchived=${showArchived}`);
      setBookings(response.data.bookings || []);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(err.response?.data?.error || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchPackageBookings = async () => {
    try {
      setPackageLoading(true);
      const response = await api.get(`/package-booking/my-bookings?includeArchived=${showArchived}`);
      setPackageBookings(response.data.bookings || []);
    } catch (err) {
      console.error('Error fetching package bookings:', err);
      // Don't set error, just show empty
    } finally {
      setPackageLoading(false);
    }
  };

  const fetchInquiries = async () => {
    try {
      setInquiriesLoading(true);
      const response = await api.get(`/package-booking/my-inquiries?includeArchived=${showArchived}`);
      setInquiries(response.data.inquiries || []);
    } catch (err) {
      console.error('Error fetching inquiries:', err);
      // Don't set error, just show empty
    } finally {
      setInquiriesLoading(false);
    }
  };

  const getPackageStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'partner_confirmed': return 'info';
      case 'payment_pending': return 'info';
      case 'paid': return 'success';
      case 'confirmed': return 'success';
      case 'cancelled': return 'error';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const getPackageStatusLabel = (status) => {
    switch (status) {
      case 'pending': return '⏳ Awaiting Agent Confirmation';
      case 'partner_confirmed': return '💳 Ready to Pay';
      case 'payment_pending': return '💳 Payment Link Sent';
      case 'paid': return '✅ Paid';
      case 'confirmed': return '🎉 Confirmed';
      case 'cancelled': return '❌ Cancelled';
      case 'completed': return '✓ Completed';
      default: return status;
    }
  };

  const fetchUserTrips = async () => {
    try {
      const response = await api.get('/trips');
      // Filter to only show trips created by the user (not trips they're just a member of)
      const createdTrips = (response.data || []).filter(trip => trip.user_role === 'creator');
      setUserTrips(createdTrips);
    } catch (err) {
      console.error('Error fetching trips:', err);
      showSnackbar('Failed to load trips', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const openPackageBooking = (booking) => {
    if (!booking?.booking_reference) return;
    navigate(`/my-bookings/packages/${booking.booking_reference}`);
  };

  const openPackageMessageThread = async (booking) => {
    if (!booking?.booking_reference) return;
    try {
      setMessagingBookingRef(booking.booking_reference);
      if (booking.inquiry_id) {
        navigate(`/my-inquiries/${booking.inquiry_id}`);
        return;
      }
      const response = await api.post(`/package-booking/my-bookings/${booking.booking_reference}/thread`);
      navigate(`/my-inquiries/${response.data.inquiryId}`);
    } catch (err) {
      console.error('Error opening booking message thread:', err);
      showSnackbar(err.response?.data?.error || 'Failed to open messages with the agent', 'error');
    } finally {
      setMessagingBookingRef(null);
    }
  };

  // Archive/unarchive package booking
  const handleArchiveBooking = async (bookingRef, archived) => {
    try {
      await api.patch(`/package-booking/my-bookings/${bookingRef}/archive`, { archived });
      showSnackbar(archived ? 'Booking archived' : 'Booking unarchived', 'success');
      fetchPackageBookings();
    } catch (err) {
      console.error('Error archiving booking:', err);
      showSnackbar('Failed to archive booking', 'error');
    }
  };

  // Archive/unarchive inquiry
  const handleArchiveInquiry = async (inquiryId, archived) => {
    try {
      await api.patch(`/package-booking/my-inquiries/${inquiryId}/archive`, { archived });
      showSnackbar(archived ? 'Inquiry archived' : 'Inquiry unarchived', 'success');
      fetchInquiries();
    } catch (err) {
      console.error('Error archiving inquiry:', err);
      showSnackbar('Failed to archive inquiry', 'error');
    }
  };

  // Delete package booking
  const handleDeletePackageBooking = async (bookingRef) => {
    try {
      await api.delete(`/package-booking/my-bookings/${bookingRef}`);
      showSnackbar('Booking deleted', 'success');
      fetchPackageBookings();
    } catch (err) {
      console.error('Error deleting package booking:', err);
      showSnackbar(err.response?.data?.error || 'Failed to delete booking', 'error');
    }
  };

  // Delete inquiry
  const handleDeleteInquiry = async (inquiryId) => {
    try {
      await api.delete(`/package-booking/my-inquiries/${inquiryId}`);
      showSnackbar('Inquiry deleted', 'success');
      fetchInquiries();
    } catch (err) {
      console.error('Error deleting inquiry:', err);
      showSnackbar(err.response?.data?.error || 'Failed to delete inquiry', 'error');
    }
  };

  // Package booking menu handlers
  const handlePackageMenuOpen = (event, booking) => {
    event.stopPropagation();
    setPackageMenuAnchor(event.currentTarget);
    setSelectedPackageBooking(booking);
  };

  const handlePackageMenuClose = () => {
    setPackageMenuAnchor(null);
  };

  // Inquiry menu handlers
  const handleInquiryMenuOpen = (event, inquiry) => {
    event.stopPropagation();
    setInquiryMenuAnchor(event.currentTarget);
    setSelectedInquiry(inquiry);
  };

  const handleInquiryMenuClose = () => {
    setInquiryMenuAnchor(null);
  };

  const handleMenuOpen = (event, booking) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleArchive = async () => {
    try {
      await api.patch(`/bookings/${selectedBooking.id}/archive`);
      showSnackbar('Booking archived successfully');
      fetchBookings();
    } catch (err) {
      console.error('Error archiving booking:', err);
      showSnackbar(err.response?.data?.error || 'Failed to archive booking', 'error');
    }
    handleMenuClose();
  };

  const handleUnarchive = async () => {
    try {
      await api.patch(`/bookings/${selectedBooking.id}/unarchive`);
      showSnackbar('Booking unarchived successfully');
      fetchBookings();
    } catch (err) {
      console.error('Error unarchiving booking:', err);
      showSnackbar(err.response?.data?.error || 'Failed to unarchive booking', 'error');
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialog(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/bookings/${selectedBooking.id}`);
      showSnackbar('Booking deleted successfully');
      fetchBookings();
      setDeleteDialog(false);
    } catch (err) {
      console.error('Error deleting booking:', err);
      showSnackbar(err.response?.data?.error || 'Failed to delete booking', 'error');
    }
  };

  const handleAddToTripClick = async (booking) => {
    setSelectedBooking(booking);
    await fetchUserTrips();
    setAddToTripDialog(true);
  };

  const handleAddToTrip = async () => {
    if (!selectedTripId) {
      showSnackbar('Please select a trip', 'warning');
      return;
    }

    try {
      await api.post(`/bookings/${selectedBooking.id}/add-to-trip`, {
        tripId: selectedTripId
      });
      showSnackbar('Flight added to trip itinerary successfully');
      setAddToTripDialog(false);
      setSelectedTripId('');
      
      // Navigate to the trip's itinerary page
      setTimeout(() => {
        navigate(`/trips/${selectedTripId}`);
      }, 1000); // Short delay to show the success message
    } catch (err) {
      console.error('Error adding to trip:', err);
      showSnackbar(err.response?.data?.error || 'Failed to add flight to trip', 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={documentPageCanvasSx}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={documentPageCanvasSx}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, py: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }

  const visibleBookings = bookings.filter((booking) => isArchivedValue(booking.archived) === showArchived);
  const visiblePackageBookings = packageBookings.filter(
    (booking) => isArchivedValue(booking.user_archived) === showArchived
  );
  const visibleInquiries = inquiries.filter(
    (inquiry) => isArchivedValue(inquiry.user_archived) === showArchived
  );

  const pendingPaymentCount = visiblePackageBookings.filter(
    b => b.status === 'partner_confirmed' && b.payment_link_url
  ).length;

  const pendingInquiryCount = visibleInquiries.filter(i => i.status === 'new').length;
  const totalPendingCount = pendingPaymentCount + pendingInquiryCount;

  const archivedChip = (
    <Chip
      icon={<ArchiveIcon sx={{ fontSize: '14px !important' }} />}
      label={t('myBookingsPage.archived')}
      size="small"
      sx={{
        ...bookingChipSx,
        bgcolor: DOCUMENT.ink,
        color: DOCUMENT.ivory,
        '& .MuiChip-icon': { color: DOCUMENT.ivory },
      }}
    />
  );

  return (
    <Box
      sx={documentPageCanvasSx}
      data-testid="my-bookings-page"
      data-theme="document"
      data-view={showArchived ? 'archived' : 'active'}
    >
    <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          {showArchived && (
            <Typography sx={{ ...documentEyebrowSx, mb: 1 }} data-testid="archived-view-label">
              {t('myBookingsPage.viewingArchived')}
            </Typography>
          )}
          <Typography component="h1" sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 32, md: 40 }, fontWeight: 400, lineHeight: 1.1, color: DOCUMENT.ink }}>
            {t('nav.myBookings')}
          </Typography>
          <Typography sx={{ color: DOCUMENT.ink3, mt: 0.75, fontSize: 14.5 }}>
            {showArchived ? t('myBookingsPage.subtitleArchived') : t('myBookingsPage.subtitle')}
          </Typography>
        </Box>
        <Button
          onClick={() => setShowArchived(!showArchived)}
          startIcon={showArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
          sx={showArchived ? partnerOxbloodBtnSx : partnerOutlineBtnSx}
          aria-pressed={showArchived}
          data-testid="show-archived-toggle"
        >
          {showArchived ? t('myBookingsPage.showActive') : t('myBookingsPage.showArchived')}
        </Button>
      </Box>

      <Box sx={{ mb: 3, bgcolor: DOCUMENT.surface, border: `1px solid ${DOCUMENT.ruleHard}` }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          variant="fullWidth"
          textColor="inherit"
          TabIndicatorProps={{ sx: documentTabIndicatorSx }}
          sx={documentSubTabsSx}
        >
          <Tab 
            icon={<Flight sx={accentIconSx} />} 
            label={t('myBookingsPage.flights')} 
            iconPosition="start"
          />
          <Tab 
            icon={<ExploreIcon sx={accentIconSx} />} 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {t('myBookingsPage.packages')}
                {totalPendingCount > 0 && (
                  <Chip 
                    label={totalPendingCount} 
                    size="small" 
                    sx={{
                      ...bookingChipSx,
                      bgcolor: DOCUMENT.oxblood,
                      color: DOCUMENT.ivory,
                    }}
                  />
                )}
              </Box>
            }
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Flight Bookings Tab */}
      {activeTab === 0 && (
        <>
          {visibleBookings.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', bgcolor: DOCUMENT.surface, border: `1px solid ${DOCUMENT.ruleHard}`, borderRadius: 0, boxShadow: 'none' }}>
          <Flight sx={{ fontSize: 64, color: DOCUMENT.ink3, mb: 2 }} />
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 28, fontWeight: 400, mb: 1 }}>
            {t(showArchived ? 'myBookingsPage.emptyArchivedFlights' : 'myBookingsPage.emptyFlights')}
          </Typography>
          <Typography sx={{ color: DOCUMENT.ink3, mb: 3 }}>
            {t(showArchived ? 'myBookingsPage.emptyArchivedFlightsHint' : 'myBookingsPage.emptyFlightsHint')}
          </Typography>
          {!showArchived && (
          <Button 
            onClick={() => navigate('/deals?tab=flights')}
            sx={partnerOxbloodBtnSx}
          >
            {t('myBookingsPage.searchFlights')}
          </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {visibleBookings.map((booking) => {
            const flightDetails = typeof booking.flight_details === 'string'
              ? JSON.parse(booking.flight_details)
              : booking.flight_details;
            const passengers = typeof booking.passengers === 'string'
              ? JSON.parse(booking.passengers)
              : booking.passengers;

            // Extract flight info with fallback for old bookings
            const origin = flightDetails?.origin || flightDetails?.slices?.[0]?.origin?.iata_code || flightDetails?.[0]?.origin?.iata_code || 'N/A';
            const destination = flightDetails?.destination || flightDetails?.slices?.[0]?.destination?.iata_code || flightDetails?.[0]?.destination?.iata_code || 'N/A';
            const departureTime = flightDetails?.departure_time || flightDetails?.slices?.[0]?.segments?.[0]?.departing_at || flightDetails?.[0]?.segments?.[0]?.departing_at;

            return (
              <Grid item xs={12} key={booking.id}>
                <Card 
                  data-archived={isArchivedValue(booking.archived) ? 'true' : 'false'}
                  sx={{ 
                    ...listCardSx(isArchivedValue(booking.archived)),
                    position: 'relative',
                  }}
                  onMouseEnter={() => setHoveredBookingId(booking.id)}
                  onMouseLeave={() => setHoveredBookingId(null)}
                  onClick={() => navigate(`/booking/${booking.id}`)}
                >
                  {/* Hover Add Button */}
                  {hoveredBookingId === booking.id && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        right: 16,
                        transform: 'translateY(-50%)',
                        zIndex: 10
                      }}
                    >
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToTripClick(booking);
                        }}
                        sx={{
                          bgcolor: DOCUMENT.oxblood,
                          color: DOCUMENT.ivory,
                          width: 56,
                          height: 56,
                          borderRadius: 0,
                          '&:hover': {
                            bgcolor: DOCUMENT.nav,
                          },
                        }}
                      >
                        <AddIcon fontSize="large" />
                      </IconButton>
                    </Box>
                  )}

                  <CardContent>
                    {/* Header with Status */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {origin} → {destination}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <span style={{ fontWeight: 600 }}>Booking Reference:</span>{' '}
                          <span style={{ 
                            color: 'var(--dvn-oxblood, #4A0E15)', 
                            fontSize: '0.95rem', 
                            fontWeight: 700,
                            letterSpacing: '0.5px'
                          }}>
                            {booking.booking_reference || booking.duffel_order_id}
                          </span>
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isArchivedValue(booking.archived) && archivedChip}
                        <Chip 
                          label={booking.status || 'confirmed'} 
                          color={getStatusColor(booking.status)}
                          sx={{ textTransform: 'capitalize' }}
                        />
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, booking)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Flight Details */}
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <CalendarToday sx={accentIconSx} />
                          <Typography variant="body2" fontWeight={600}>Departure</Typography>
                        </Box>
                        <Typography variant="body1">
                          {formatDate(departureTime || booking.created_at)}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Flight sx={accentIconSx} />
                          <Typography variant="body2" fontWeight={600}>Airline</Typography>
                        </Box>
                        <Typography variant="body1">
                          {flightDetails?.airline || flightDetails?.slices?.[0]?.segments?.[0]?.operating_carrier?.name || 'See details'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Person sx={accentIconSx} />
                          <Typography variant="body2" fontWeight={600}>Passengers</Typography>
                        </Box>
                        <Typography variant="body1">
                          {passengers?.length || 1} passenger{passengers?.length !== 1 ? 's' : ''}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <AttachMoney sx={accentIconSx} />
                          <Typography variant="body2" fontWeight={600}>Total</Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: DOCUMENT.oxblood }}>
                          {booking.customer_paid_currency || booking.currency || 'USD'} {parseFloat(booking.customer_paid_amount || booking.total_amount || 0).toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Additional Info */}
                    <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary">
                        Booked on: {formatDate(booking.created_at)}
                      </Typography>
                      {booking.profit_amount && (
                        <Typography variant="caption" color="text.secondary">
                          Service Fee: {booking.currency || 'USD'} {parseFloat(booking.profit_amount).toFixed(2)}
                        </Typography>
                      )}
                    </Box>

                    {/* Action Button */}
                    <Box sx={{ mt: 2 }}>
                      <Button 
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/booking/${booking.id}`);
                        }}
                        sx={partnerOutlineBtnSx}
                      >
                        {t('myInquiries.viewDetails')}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
        </>
      )}

      {/* Package Bookings Tab */}
      {activeTab === 1 && (
        <>
          {/* Active Inquiries Section */}
          {!inquiriesLoading && visibleInquiries.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 26, fontWeight: 400, mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: DOCUMENT.ink }}>
                {t(showArchived ? 'myBookingsPage.archivedInquiries' : 'nav.inquiries')}
                <Chip
                  label={visibleInquiries.length}
                  size="small"
                  sx={{ ...bookingChipSx, bgcolor: DOCUMENT.oxblood, color: DOCUMENT.ivory }}
                />
              </Typography>
              <Grid container spacing={2}>
                {visibleInquiries.map((inquiry) => (
                  <Grid item xs={12} sm={6} md={4} key={inquiry.id}>
                    <Card 
                      data-archived={isArchivedValue(inquiry.user_archived) ? 'true' : 'false'}
                      sx={{ 
                        ...listCardSx(isArchivedValue(inquiry.user_archived)),
                        ...(!isArchivedValue(inquiry.user_archived) && inquiry.status === 'new'
                          ? { borderColor: DOCUMENT.accent }
                          : {}),
                      }}
                      onClick={() => navigate(`/my-inquiries/${inquiry.id}`)}
                    >
                      <Box sx={{ position: 'relative' }}>
                        {inquiry.cover_image ? (
                          <Box
                            component="img"
                            src={inquiry.cover_image}
                            sx={{ width: '100%', height: 120, objectFit: 'cover' }}
                          />
                        ) : (
                          <Box sx={{ 
                            width: '100%', 
                            height: 120, 
                            bgcolor: DOCUMENT.canvas,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <ExploreIcon sx={{ fontSize: 48, color: DOCUMENT.ink3 }} />
                          </Box>
                        )}
                        <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.75 }}>
                          {isArchivedValue(inquiry.user_archived) && archivedChip}
                          <Chip
                            label={inquiry.status === 'new' ? '⏳ Awaiting Response' : '💬 Agent Replied'}
                            size="small"
                            color={inquiry.status === 'new' ? 'warning' : 'success'}
                            sx={bookingChipSx}
                          />
                        </Box>
                      </Box>
                      <CardContent sx={{ py: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                              {getInquiryDisplayTitle(inquiry, t('myInquiries.customTripRequest'))}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {[inquiry.partner_name, inquiry.destination, inquiry.duration_days ? t('myInquiries.days', { count: inquiry.duration_days }) : null].filter(Boolean).join(' · ')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sent {new Date(inquiry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => handleInquiryMenuOpen(e, inquiry)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Divider sx={{ mt: 3 }} />
            </Box>
          )}

          {packageLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
            </Box>
          ) : visiblePackageBookings.length === 0 && visibleInquiries.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center', bgcolor: DOCUMENT.surface, border: `1px solid ${DOCUMENT.ruleHard}`, borderRadius: 0, boxShadow: 'none' }}>
              <ExploreIcon sx={{ fontSize: 64, color: DOCUMENT.ink3, mb: 2 }} />
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 28, fontWeight: 400, mb: 1 }}>
                {t(showArchived ? 'myBookingsPage.emptyArchivedPackages' : 'myBookingsPage.emptyPackages')}
              </Typography>
              <Typography sx={{ color: DOCUMENT.ink3, mb: 3 }}>
                {t(showArchived ? 'myBookingsPage.emptyArchivedPackagesHint' : 'myBookingsPage.emptyPackagesHint')}
              </Typography>
              {!showArchived && (
              <Button 
                onClick={() => navigate('/deals-plan?tab=packages')}
                sx={partnerOxbloodBtnSx}
              >
                {t('myBookingsPage.browsePackages')}
              </Button>
              )}
            </Paper>
          ) : visiblePackageBookings.length === 0 ? null : (
            <>
            {visibleInquiries.length > 0 && (
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 26, fontWeight: 400, mb: 2, mt: 2, color: DOCUMENT.ink }}>
                {t(showArchived ? 'myBookingsPage.archivedConfirmed' : 'myBookingsPage.confirmed')}
              </Typography>
            )}
            <Grid container spacing={3}>
              {visiblePackageBookings.map((booking) => (
                <Grid item xs={12} key={booking.id}>
                  <Card 
                    data-archived={isArchivedValue(booking.user_archived) ? 'true' : 'false'}
                    sx={{ 
                      ...listCardSx(isArchivedValue(booking.user_archived)),
                      position: 'relative',
                      ...(!isArchivedValue(booking.user_archived) && booking.status === 'partner_confirmed' && {
                        borderColor: DOCUMENT.accent,
                      })
                    }}
                    onClick={() => openPackageBooking(booking)}
                  >
                    <CardContent>
                      {/* Payment Required Banner */}
                      {booking.status === 'partner_confirmed' && booking.payment_link_url && (
                        <Alert 
                          severity="warning" 
                          sx={{ mb: 2 }}
                          action={
                            <Button
                              size="small"
                              startIcon={<PaymentIcon />}
                              sx={partnerOxbloodBtnSx}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(booking.payment_link_url, '_blank');
                              }}
                            >
                              Pay Now
                            </Button>
                          }
                        >
                          <strong>Payment Required</strong> - The agent has confirmed your booking. Complete payment to secure your reservation.
                        </Alert>
                      )}

                      {/* Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          {booking.cover_image ? (
                            <Box
                              component="img"
                              src={booking.cover_image}
                              sx={{ 
                                width: 80, 
                                height: 60, 
                                objectFit: 'cover', 
                                borderRadius: 0 
                              }}
                            />
                          ) : (
                            <Avatar sx={{ width: 60, height: 60, bgcolor: DOCUMENT.oxblood }}>
                              <ExploreIcon />
                            </Avatar>
                          )}
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {booking.package_title}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                              <LocationOn fontSize="small" />
                              <Typography variant="body2">
                                {booking.destination} • {booking.duration_days} days
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              <strong>Ref:</strong>{' '}
                              <span style={{ 
                                color: 'var(--dvn-oxblood, #4A0E15)', 
                                fontWeight: 700,
                                letterSpacing: '0.5px'
                              }}>
                                {booking.booking_reference}
                              </span>
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {isArchivedValue(booking.user_archived) && archivedChip}
                          {isPackageExpired({ available_until: booking.package_available_until }) ? (
                            <Chip label={t('myPackageBooking.expired')} variant="outlined" />
                          ) : null}
                          <Chip
                            label={getPackageStatusLabel(booking.status)}
                            color={getPackageStatusColor(booking.status)}
                          />
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* Booking Details */}
                      <Grid container spacing={3}>
                        {/* Trip Dates */}
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Flight sx={accentIconSx} />
                            <Typography variant="body2" fontWeight={600}>Trip Date</Typography>
                          </Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {booking.travel_date 
                              ? new Date(booking.travel_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
                              : 'Not set'}
                            {booking.return_date && (
                              <> → {new Date(booking.return_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</>
                            )}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Person sx={accentIconSx} />
                            <Typography variant="body2" fontWeight={600}>Travelers</Typography>
                          </Box>
                          <Typography variant="body1">
                            {booking.num_travelers || 1} traveler{(booking.num_travelers || 1) !== 1 ? 's' : ''}
                          </Typography>
                          {booking.travelers && (() => {
                            try {
                              const passengers = typeof booking.travelers === 'string' 
                                ? JSON.parse(booking.travelers) 
                                : booking.travelers;
                              if (Array.isArray(passengers) && passengers.length > 0) {
                                return (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {passengers.map(p => `${p.firstName} ${p.lastName}`).join(', ')}
                                  </Typography>
                                );
                              }
                            } catch (e) {}
                            return null;
                          })()}
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <CalendarToday sx={accentIconSx} />
                            <Typography variant="body2" fontWeight={600}>Booked On</Typography>
                          </Box>
                          <Typography variant="body1">
                            {formatDate(booking.created_at)}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <AttachMoney sx={accentIconSx} />
                            <Typography variant="body2" fontWeight={600}>Total</Typography>
                          </Box>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: DOCUMENT.oxblood }}>
                            {booking.currency || 'USD'} ${parseFloat(booking.total_price || 0).toLocaleString()}
                          </Typography>
                        </Grid>

                      </Grid>

                      {/* Agent Details Section */}
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          mt: 2, 
                          p: 2, 
                          bgcolor: DOCUMENT.canvas,
                          borderRadius: 0,
                          borderColor: DOCUMENT.ruleHard,
                          boxShadow: 'none',
                        }}
                      >
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            mb: 1.5,
                            color: 'inherit',
                          }}
                        >
                          {booking.partner_logo ? (
                            <PartnerLockup
                              src={booking.partner_logo}
                              alt={booking.partner_name || ''}
                              height={28}
                              maxWidth={132}
                              sx={{ px: 1, py: 0.5 }}
                            />
                          ) : (
                            <Avatar sx={{ width: 40, height: 40, bgcolor: 'var(--dvn-oxblood, #4A0E15)' }}>
                              {booking.partner_name?.charAt(0).toUpperCase() || 'P'}
                            </Avatar>
                          )}
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {booking.partner_name || 'Travel Agent'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ⭐ {brandedClient ? t('publicPackage.verifiedPartner') : t('myPackageBooking.verifiedPartner')}
                            </Typography>
                          </Box>
                          {booking.partner_website && (
                            <IconButton
                              size="small"
                              component="a"
                              href={booking.partner_website}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              aria-label="Partner website"
                            >
                              <OpenInNew sx={{ fontSize: 16, opacity: 0.7 }} />
                            </IconButton>
                          )}
                        </Box>
                        
                        <Grid container spacing={1}>
                          {booking.partner_contact_name && (
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Person fontSize="small" color="action" sx={{ mt: 0.3 }} />
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {booking.partner_contact_name}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          )}
                        </Grid>

                        {/* Partner Confirmation */}
                        {booking.partner_confirmation_id && (
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary">
                              Agent Confirmation: <strong style={{ color: 'var(--dvn-oxblood, #4A0E15)' }}>{booking.partner_confirmation_id}</strong>
                            </Typography>
                          </Box>
                        )}
                      </Paper>

                      {/* Agent Notes */}
                      {booking.partner_notes && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <strong>Agent Notes:</strong> {booking.partner_notes}
                        </Alert>
                      )}

                      {/* Action Buttons */}
                      <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {booking.status === 'partner_confirmed' && booking.payment_link_url && (
                          <>
                            <Button
                              startIcon={<PaymentIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(booking.payment_link_url, '_blank');
                              }}
                              sx={partnerOxbloodBtnSx}
                            >
                              Complete Payment - ${parseFloat(booking.total_price || 0).toLocaleString()}
                            </Button>
                            {isDevelopment && (
                              <Button
                                variant="outlined"
                                color="success"
                                disabled={testingPayment === booking.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTestPayment(booking);
                                }}
                                sx={{ 
                                  borderStyle: 'dashed',
                                  '&:hover': { borderStyle: 'solid' }
                                }}
                              >
                                {testingPayment === booking.id ? 'Processing...' : '🧪 Test: Confirm Payment'}
                              </Button>
                            )}
                          </>
                        )}
                        
                        {(booking.trip_id || booking.package_slug) && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(booking.trip_id
                                ? `/trips/${booking.trip_id}`
                                : `/packages/${booking.package_slug}`);
                            }}
                            sx={partnerOxbloodBtnSx}
                          >
                            {t('myPackageBooking.viewItinerary')}
                          </Button>
                        )}

                        <Button
                          startIcon={<ChatBubbleOutline />}
                          onClick={(e) => {
                            e.stopPropagation();
                            openPackageMessageThread(booking);
                          }}
                          disabled={messagingBookingRef === booking.booking_reference}
                          sx={partnerOxbloodBtnSx}
                        >
                          {messagingBookingRef === booking.booking_reference
                            ? t('myPackageBooking.openingMessages')
                            : t('myPackageBooking.messageAgent')}
                        </Button>

                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            openPackageBooking(booking);
                          }}
                          sx={partnerOutlineBtnSx}
                        >
                          {t('myInquiries.viewDetails')}
                        </Button>

                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePackageMenuOpen(e, booking);
                          }}
                          sx={{ ml: 1 }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            </>
          )}
        </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedBooking?.archived ? (
          <MenuItem onClick={handleUnarchive}>
            <UnarchiveIcon sx={{ mr: 1 }} fontSize="small" />
            Unarchive
          </MenuItem>
        ) : (
          <MenuItem onClick={handleArchive}>
            <ArchiveIcon sx={{ mr: 1 }} fontSize="small" />
            Archive
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Package Booking Action Menu */}
      <Menu
        anchorEl={packageMenuAnchor}
        open={Boolean(packageMenuAnchor)}
        onClose={handlePackageMenuClose}
      >
        <MenuItem onClick={() => {
          handleArchiveBooking(selectedPackageBooking?.booking_reference, !selectedPackageBooking?.user_archived);
          handlePackageMenuClose();
        }}>
          {selectedPackageBooking?.user_archived ? (
            <>
              <UnarchiveIcon sx={{ mr: 1 }} fontSize="small" />
              Unarchive
            </>
          ) : (
            <>
              <ArchiveIcon sx={{ mr: 1 }} fontSize="small" />
              Archive
            </>
          )}
        </MenuItem>
        <MenuItem 
          onClick={() => {
            setPackageDeleteDialog(true);
            handlePackageMenuClose();
          }} 
          sx={{ color: 'error.main' }}
          disabled={!['cancelled', 'completed', 'refunded'].includes(selectedPackageBooking?.status)}
        >
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Inquiry Action Menu */}
      <Menu
        anchorEl={inquiryMenuAnchor}
        open={Boolean(inquiryMenuAnchor)}
        onClose={handleInquiryMenuClose}
      >
        <MenuItem onClick={() => {
          handleArchiveInquiry(selectedInquiry?.id, !selectedInquiry?.user_archived);
          handleInquiryMenuClose();
        }}>
          {selectedInquiry?.user_archived ? (
            <>
              <UnarchiveIcon sx={{ mr: 1 }} fontSize="small" />
              Unarchive
            </>
          ) : (
            <>
              <ArchiveIcon sx={{ mr: 1 }} fontSize="small" />
              Archive
            </>
          )}
        </MenuItem>
        <MenuItem 
          onClick={() => {
            setInquiryDeleteDialog(true);
            handleInquiryMenuClose();
          }} 
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Package Booking Delete Confirmation Dialog */}
      <Dialog open={packageDeleteDialog} onClose={() => setPackageDeleteDialog(false)}>
        <DialogTitle>Delete Package Booking?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this package booking? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackageDeleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              handleDeletePackageBooking(selectedPackageBooking?.booking_reference);
              setPackageDeleteDialog(false);
            }} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inquiry Delete Confirmation Dialog */}
      <Dialog open={inquiryDeleteDialog} onClose={() => setInquiryDeleteDialog(false)}>
        <DialogTitle>Delete Inquiry?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this inquiry? This will also delete all messages. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInquiryDeleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              handleDeleteInquiry(selectedInquiry?.id);
              setInquiryDeleteDialog(false);
            }} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add to Trip Dialog */}
      <Dialog 
        open={addToTripDialog} 
        onClose={() => setAddToTripDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Flight to Trip</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a trip to add this flight to the itinerary. The flight dates must align with the trip dates.
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Trip</InputLabel>
            <Select
              value={selectedTripId}
              onChange={(e) => setSelectedTripId(e.target.value)}
              label="Select Trip"
            >
              {userTrips.map((trip) => (
                <MenuItem key={trip.id} value={trip.id}>
                  {trip.title} ({new Date(trip.start_date).toLocaleDateString()} - {new Date(trip.end_date).toLocaleDateString()})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddToTripDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddToTrip} 
            variant="contained"
            disabled={!selectedTripId}
          >
            Add to Trip
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
      >
        <DialogTitle>Delete Booking?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete this booking? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
    </Box>
  );
};

export default MyBookings;

